import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layers, Plus, Copy, Check, RefreshCw, ArrowLeft, Pencil, Archive, Loader2, ClipboardList, FileText, Mic, Users } from "lucide-react";
import api from "../../api/api";
import {
    Alert, EmptyState, Field, OrgModal, Panel, Pill, apiError, formatDate, useOrgStyles,
} from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

export default function OrgPrograms() {
    const { orgId, org, isOrgAdmin, reloadOrg, goTo } = useOrgPortal();
    const s = useOrgStyles();
    const [searchParams, setSearchParams] = useSearchParams();
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | {} (new) | program
    const selectedId = Number(searchParams.get("programId")) || null;

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/programs`);
            setPrograms(res.data.programs || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load programs."));
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const select = (id) => {
        const next = new URLSearchParams(searchParams);
        if (id) next.set("programId", id); else next.delete("programId");
        setSearchParams(next);
    };

    const archive = async (program) => {
        if (!window.confirm(`Archive "${program.ProgramName}"? Its ${program.EnrolledCount} enrollment(s) end and the invitation code stops working.`)) return;
        try {
            await api.delete(`/org/${orgId}/programs/${program.ProgramID}`);
            select(null);
            await Promise.all([load(), reloadOrg()]);
        } catch (err) {
            setError(apiError(err));
        }
    };

    const atLimit = org.ProgramCount >= org.ProgramLimit;

    if (selectedId) {
        return (
            <ProgramDashboard
                orgId={orgId}
                programId={selectedId}
                isOrgAdmin={isOrgAdmin}
                onBack={() => select(null)}
                onEdit={(p) => setEditing(p)}
                onArchive={archive}
                goTo={goTo}
                editor={editing && (
                    <ProgramForm orgId={orgId} program={editing} onClose={() => setEditing(null)}
                                 onSaved={async () => { setEditing(null); await load(); }} />
                )}
            />
        );
    }

    return (
        <>
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Panel
                title="Programs"
                padded={false}
                actions={isOrgAdmin && (
                    <button type="button" className={s.btnPrimary} onClick={() => setEditing({})} disabled={atLimit}
                            title={atLimit ? `Program limit (${org.ProgramLimit}) reached` : undefined}>
                        <Plus size={16} /> New program
                    </button>
                )}
            >
                {isOrgAdmin && atLimit && (
                    <p className={`px-5 pt-4 text-sm ${s.muted}`}>
                        You've reached your program limit ({org.ProgramLimit}). Archive a program or contact Build My Biz Brand to add more.
                    </p>
                )}
                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : programs.length === 0 ? (
                    <EmptyState
                        icon={Layers}
                        title="No programs yet"
                        body="Programs group students and give them an invitation code to join. Students in a program can use its custom resume and interview criteria."
                        action={isOrgAdmin && <button type="button" className={s.btnPrimary} onClick={() => setEditing({})}><Plus size={16} /> Create a program</button>}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Program</th>
                                    <th className={s.th}>Dates</th>
                                    <th className={s.th}>Students</th>
                                    <th className={s.th}>Invitation code</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {programs.map((p) => (
                                    <tr key={p.ProgramID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={s.td}>
                                            <button type="button" onClick={() => select(p.ProgramID)} className={`text-left font-semibold ${s.text} hover:underline`}>
                                                {p.ProgramName}
                                            </button>
                                            {p.Outcome && <div className={`mt-0.5 line-clamp-1 text-xs ${s.muted}`}>{p.Outcome}</div>}
                                        </td>
                                        <td className={`${s.td} whitespace-nowrap`}><ProgramDates program={p} /></td>
                                        <td className={`${s.td} tabular-nums`}>{p.EnrolledCount}</td>
                                        <td className={s.td}><InviteCode code={p.InviteCode} /></td>
                                        <td className={`${s.td} text-right`}>
                                            <div className="flex justify-end gap-1">
                                                <button type="button" className={s.btnLink} onClick={() => select(p.ProgramID)}>View</button>
                                                {isOrgAdmin && (
                                                    <button type="button" className={`${s.btnLink} ml-3`} onClick={() => setEditing(p)}>Edit</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
            {editing && (
                <ProgramForm orgId={orgId} program={editing} onClose={() => setEditing(null)}
                             onSaved={async (saved) => {
                                 setEditing(null);
                                 await Promise.all([load(), reloadOrg()]);
                                 if (saved && !editing.ProgramID) select(saved.ProgramID);
                             }} />
            )}
        </>
    );
}

function ProgramDates({ program }) {
    const s = useOrgStyles();
    if (!program.StartDate && !program.EndDate) return <span className={s.muted}>Ongoing</span>;
    return <>{formatDate(program.StartDate) || "…"} – {formatDate(program.EndDate) || "ongoing"}</>;
}

export function InviteCode({ code, onRegenerate, regenerating }) {
    const s = useOrgStyles();
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked: the code is still visible to copy by hand */ }
    };
    return (
        <span className="inline-flex items-center gap-1.5">
            <code className={`rounded-md px-2 py-1 font-mono text-sm font-semibold tracking-wider ${s.dark ? "bg-slate-700 text-slate-100" : "bg-slate-100 text-slate-800"}`}>{code}</code>
            <button type="button" onClick={copy} aria-label={`Copy invitation code ${code}`} className={`rounded-md p-1.5 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-100"}`}>
                {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} className={s.muted} />}
            </button>
            {onRegenerate && (
                <button type="button" onClick={onRegenerate} disabled={regenerating} aria-label="Generate a new invitation code"
                        title="Generate a new code (the old one stops working)"
                        className={`rounded-md p-1.5 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-100"}`}>
                    <RefreshCw size={14} className={`${s.muted} ${regenerating ? "animate-spin" : ""}`} />
                </button>
            )}
        </span>
    );
}

function ProgramForm({ orgId, program, onClose, onSaved }) {
    const s = useOrgStyles();
    const isNew = !program.ProgramID;
    const [form, setForm] = useState({
        programName: program.ProgramName || "",
        description: program.Description || "",
        startDate: program.StartDate ? String(program.StartDate).slice(0, 10) : "",
        endDate: program.EndDate ? String(program.EndDate).slice(0, 10) : "",
        outcome: program.Outcome || "",
        inviteCode: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        const payload = { ...form, startDate: form.startDate || null, endDate: form.endDate || null };
        if (!payload.inviteCode) delete payload.inviteCode;
        try {
            const res = isNew
                ? await api.post(`/org/${orgId}/programs`, payload)
                : await api.put(`/org/${orgId}/programs/${program.ProgramID}`, payload);
            await onSaved(res.data.program);
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal
            isOpen
            onClose={onClose}
            title={isNew ? "New program" : "Edit program"}
            footer={<>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="program-form" className={s.btnPrimary} disabled={saving}>
                    {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create program" : "Save"}
                </button>
            </>}
        >
            <Alert>{error}</Alert>
            <form id="program-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
                <Field label="Program name" htmlFor="pf-name" className="sm:col-span-2">
                    <input id="pf-name" className={s.input} value={form.programName} onChange={set("programName")} required maxLength={200} autoFocus />
                </Field>
                <Field label="Start date" htmlFor="pf-start">
                    <input id="pf-start" type="date" className={s.input} value={form.startDate} onChange={set("startDate")} />
                </Field>
                <Field label="End date" htmlFor="pf-end" hint="Students can't join after this date.">
                    <input id="pf-end" type="date" className={s.input} value={form.endDate} min={form.startDate || undefined} onChange={set("endDate")} />
                </Field>
                <Field label="Outcome" htmlFor="pf-outcome" className="sm:col-span-2" hint="What students should achieve, e.g. “Land a summer internship”.">
                    <input id="pf-outcome" className={s.input} value={form.outcome} onChange={set("outcome")} maxLength={1000} />
                </Field>
                <Field label="Description" htmlFor="pf-desc" className="sm:col-span-2">
                    <textarea id="pf-desc" rows={3} className={s.input} value={form.description} onChange={set("description")} maxLength={1000} />
                </Field>
                <Field label={isNew ? "Invitation code (optional)" : "New invitation code (optional)"} htmlFor="pf-code" className="sm:col-span-2"
                       hint={isNew ? "Leave blank to generate one. 4–32 letters, numbers or dashes." : "Leave blank to keep the current code."}>
                    <input id="pf-code" className={`${s.input} font-mono uppercase`} value={form.inviteCode}
                           onChange={(e) => setForm((f) => ({ ...f, inviteCode: e.target.value.toUpperCase() }))}
                           pattern="[A-Za-z0-9-]{4,32}" maxLength={32} placeholder={program.InviteCode || "e.g. SPRING26"} />
                </Field>
            </form>
        </OrgModal>
    );
}

function ProgramDashboard({ orgId, programId, isOrgAdmin, onBack, onEdit, onArchive, goTo, editor }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [regenerating, setRegenerating] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/programs/${programId}`);
            setData(res.data);
        } catch (err) {
            setError(apiError(err, "Couldn't load this program."));
        }
    }, [orgId, programId]);

    // Loads on mount and again whenever the edit modal closes.
    const editorOpen = !!editor;
    useEffect(() => { if (!editorOpen) load(); }, [editorOpen, load]);

    const regenerate = async () => {
        if (!window.confirm("Generate a new invitation code? The current code stops working immediately.")) return;
        setRegenerating(true);
        try {
            await api.post(`/org/${orgId}/programs/${programId}/invite-code`);
            await load();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setRegenerating(false);
        }
    };

    if (!data) {
        return (
            <div>
                <button type="button" onClick={onBack} className={`mb-4 inline-flex items-center gap-1 ${s.btnLink}`}><ArrowLeft size={16} /> All programs</button>
                <Alert>{error}</Alert>
                {!error && <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>}
            </div>
        );
    }

    const { program, assessments, criteria, detailsAvailable } = data;
    return (
        <div className="space-y-6">
            <button type="button" onClick={onBack} className={`inline-flex items-center gap-1 ${s.btnLink}`}><ArrowLeft size={16} /> All programs</button>
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Panel
                title={program.ProgramName}
                actions={isOrgAdmin && <>
                    <button type="button" className={s.btnSecondary} onClick={() => onEdit(program)}><Pencil size={14} /> Edit</button>
                    <button type="button" className={s.btnDanger} onClick={() => onArchive(program)}><Archive size={14} /> Archive</button>
                </>}
            >
                <div className="grid gap-5 md:grid-cols-3">
                    <div className="md:col-span-2 space-y-3">
                        {program.Outcome && <p className={s.body}><span className={`font-semibold ${s.text}`}>Outcome: </span>{program.Outcome}</p>}
                        {program.Description && <p className={`text-sm ${s.body}`}>{program.Description}</p>}
                        <p className={`text-sm ${s.muted}`}><ProgramDates program={program} /></p>
                    </div>
                    <div className={`rounded-xl border p-4 ${s.subPanel}`}>
                        <div className={`mb-2 text-xs font-semibold uppercase tracking-wide ${s.muted}`}>Invitation code</div>
                        <InviteCode code={program.InviteCode} onRegenerate={isOrgAdmin ? regenerate : undefined} regenerating={regenerating} />
                        <p className={`mt-2 text-xs ${s.muted}`}>Students enter this under My Coaching → Programs.</p>
                        <button type="button" className={`mt-3 inline-flex items-center gap-1 ${s.btnLink}`}
                                onClick={() => goTo("students", { programId: String(program.ProgramID) })}>
                            <Users size={14} /> {program.EnrolledCount} student{program.EnrolledCount === 1 ? "" : "s"} enrolled
                        </button>
                    </div>
                </div>
            </Panel>

            <Panel title={<span className="inline-flex items-center gap-2"><ClipboardList size={18} /> Assessments</span>}>
                {assessments.length === 0 ? (
                    <p className={`text-sm ${s.muted}`}>No assessments are assigned to this program. Build My Biz Brand can add custom assessments and surveys for your organization or this program.</p>
                ) : (
                    <ul className={`divide-y ${s.divider}`}>
                        {assessments.map((a) => (
                            <li key={a.SurveyID} className="flex flex-wrap items-center justify-between gap-2 py-3">
                                <div>
                                    <div className={`font-semibold ${s.text}`}>{a.Title}</div>
                                    {a.Description && <div className={`text-sm ${s.muted}`}>{a.Description}</div>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Pill>{a.Scope === "program" ? "This program" : "Whole organization"}</Pill>
                                    {detailsAvailable && (
                                        <Pill tone="primary">{a.CompletedCount} / {program.EnrolledCount} completed</Pill>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Panel>

            <div className="grid gap-6 lg:grid-cols-2">
                <CriteriaPanel icon={FileText} title="Resume evaluation criteria" criteria={criteria.resume} />
                <CriteriaPanel icon={Mic} title="Interview evaluation criteria" criteria={criteria.interview} />
            </div>
            {editor}
        </div>
    );
}

const SCOPE_LABEL = { program: "Custom for this program", organization: "Custom for your organization", default: "Build My Biz Brand default" };

function CriteriaPanel({ icon: Icon, title, criteria }) {
    const s = useOrgStyles();
    return (
        <Panel title={<span className="inline-flex items-center gap-2"><Icon size={18} /> {title}</span>}
               actions={<Pill tone={criteria.Scope === "default" ? "neutral" : "primary"}>{SCOPE_LABEL[criteria.Scope]}</Pill>}>
            {criteria.Scope !== "default" && <p className={`mb-3 text-sm font-semibold ${s.text}`}>{criteria.Name}</p>}
            {criteria.Description && <p className={`mb-3 text-sm ${s.body}`}>{criteria.Description}</p>}
            <ul className="space-y-2">
                {(criteria.categories || []).map((c) => (
                    <li key={c.Name}>
                        <div className="flex items-center justify-between gap-3 text-sm">
                            <span className={`font-medium ${s.text}`}>{c.Name}</span>
                            <span className={`tabular-nums ${s.muted}`}>{c.Weight}%</span>
                        </div>
                        <div className={`mt-1 h-1.5 overflow-hidden rounded-full ${s.dark ? "bg-slate-700" : "bg-slate-100"}`}>
                            <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, c.Weight)}%` }} />
                        </div>
                        {c.Description && <p className={`mt-1 text-xs ${s.muted}`}>{c.Description}</p>}
                    </li>
                ))}
            </ul>
            {criteria.AdditionalInstructions && (
                <p className={`mt-4 rounded-lg border p-3 text-xs ${s.subPanel} ${s.body}`}>
                    <span className="font-semibold">Additional guidance: </span>{criteria.AdditionalInstructions}
                </p>
            )}
        </Panel>
    );
}
